import { useInfiniteQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Entry } from "contentful";
import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { InstantSearchServerState } from "react-instantsearch";

import HighlightedText from "~/components/common/highlighted-text";
import { useCategory } from "~/contexts/category";
import { useConfiguration } from "~/contexts/configuration";
import { useServerSideProps } from "~/contexts/server-side-props";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import evaUtils from "~/utils/eva-utils";

import BreakpointAwareAsset from "../common/breakpoint-aware-asset";
import Container from "../common/container";
import EscapeNewLine from "../common/escape-new-line";
import Grid from "../common/grid";
import EditorialEntry from "../editorials/editorial-entry";
import EditorialLink from "../editorials/editorial-link/editorial-link";
import Breadcrumbs, { Breadcrumb } from "../product/product-breadcrumbs";
import ProductNavigationItemsCurrentPath from "../product/product-navigation-items-current-path";
import EvaProductListing from "./eva-product-listing";
import styles from "./product-grid.module.scss";
import ProductGridCustomBackground from "./product-grid-custom-background";
import ProductGridSkeleton from "./product-grid-skeleton";

export function NumberProducts({ total, type }: { total: number; type: "mobile" | "desktop" }) {
  const t = useTranslations();

  return (
    <>
      {total > 0 ? (
        <div className={classNames(styles.prodCount, type === "mobile" ? styles.mobileOnly : styles.desktopOnly)}>
          <p>{t("generic.n_products", { number: total })}</p>
        </div>
      ) : (
        <div className={classNames(styles.noResults, type === "mobile" ? styles.mobileOnly : styles.desktopOnly)}>
          <h2>{t("generic.noresults")}</h2>
          <p dangerouslySetInnerHTML={{ __html: t("generic.noresultsparagraph") }} />
        </div>
      )}
    </>
  );
}

type Props = {
  breadcrumbs: Breadcrumb[];
  components?: Entry<Editorials.GridElement>[];
  componentsAfterListing?: Entry<unknown>[];
  navigation: Entry<unknown>;
  changeHeaderLayout?: boolean;
  backgroundColors?: string[];
  setId?: string;
  listScope: string;
  initialData?: string | null;
  serverState?: InstantSearchServerState | null;
  serverUrl: string | null;
  algoliaUserToken: string;
};

export default function ProductGrid(props: Props) {
  const t = useTranslations();
  const { algoliaIndexId } = useConfiguration();
  const { eva } = useConfiguration();
  const { sort, category } = useCategory();
  const { serverSideProps } = useServerSideProps();

  const query = useInfiniteQuery({
    queryKey: ["productListing", category.fields.categoryId, props.setId, sort],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) => {
      if (props.setId) {
        return evaUtils.getProductsBySetWithChildren(eva, props.setId, sort, pageParam);
      } else {
        return evaUtils.getProductsByCategoryWithChildren(eva, category.fields.categoryId, sort, pageParam);
      }
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.Total! > lastPage.Page! * lastPage.PageSize!) {
        return lastPage.Page! + 1;
      }

      return undefined;
    },
    enabled: !props.serverState && (!!category.fields.categoryId || props.setId !== undefined),
    initialData: () => {
      if (props.initialData) {
        // Verify that this initial data was created for this specific query key
        const initialKey = serverSideProps.initialKey;
        const currentKey = JSON.stringify(["productListing", category.fields.categoryId, props.setId, sort]);

        if (initialKey === currentKey) {
          return { pages: [JSON.parse(props.initialData)], pageParams: [1] };
        }
      }

      return undefined;
    },
  });

  const totalNumberProducts = props.serverState
    ? props.serverState.initialResults[algoliaIndexId].results?.[0].nbHits
    : query?.data?.pages[0].Total && query.data?.pages[0].Total;

  const [showMore, setShowMore] = useState(false);

  return (
    <div className={styles.pageWrapper}>
      {props.backgroundColors && (
        <ProductGridCustomBackground
          color1={props.backgroundColors[0]}
          color2={props.backgroundColors[1] ? props.backgroundColors[1] : props.backgroundColors[0]}
          changeHeaderLayout={props.changeHeaderLayout}
          defaultDesktopBackground
          hasBannerHeader={!!category.fields.bannerHeader}
          type="desktop"
        />
      )}
      {category.fields.bannerHeader ? (
        <div className={styles.bannerHeaderWrapper}>
          <BreakpointAwareAsset entry={category.fields.bannerHeader} />
          <div className={styles.textWrapper}>
            {category.fields.previousPageLink ? <EditorialLink entry={category.fields.previousPageLink} /> : null}
            <h1>
              <HighlightedText text={category.fields?.formattedName ?? category.fields.name} />
            </h1>
            {category.fields.subtitle ? (
              <div className={styles.subtitle}>
                <EscapeNewLine text={category.fields.subtitle} />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <Container>
        <Grid className={styles.grid}>
          <div
            className={classNames(
              styles.headingSection,
              props.changeHeaderLayout ? styles.customHeadingSection : undefined
            )}
          >
            {props.backgroundColors && (
              <ProductGridCustomBackground
                color1={props.backgroundColors[0]}
                color2={props.backgroundColors[1] ? props.backgroundColors[1] : props.backgroundColors[0]}
                changeHeaderLayout={props.changeHeaderLayout}
                hasBannerHeader={!!category.fields.bannerHeader}
                type="mobile"
              />
            )}
            <Container
              className={classNames(
                styles.headingColumn,
                props.changeHeaderLayout ? styles.customHeadingColumn : undefined
              )}
            >
              {props.breadcrumbs && props.breadcrumbs.length > 0 ? (
                <Breadcrumbs breadcrumbs={props.breadcrumbs} />
              ) : null}
              {!category.fields.bannerHeader ? (
                <>
                  {category.fields.previousPageLink ? <EditorialLink entry={category.fields.previousPageLink} /> : null}
                  <h1>
                    <HighlightedText text={category.fields?.formattedName ?? category.fields.name} />{" "}
                  </h1>
                </>
              ) : null}
              {category.fields.subtitle && !category.fields.bannerHeader ? (
                <div className={styles.subtitle}>
                  <EscapeNewLine text={category.fields.subtitle} />
                </div>
              ) : null}
              {category.fields.description || category.fields.readMoreDescription ? (
                <div>
                  {category.fields.description ? (
                    <p className={styles.description}>
                      <EscapeNewLine text={category.fields.description} />
                    </p>
                  ) : null}
                  {category.fields.readMoreDescription ? (
                    <p
                      className={
                        showMore ? classNames(styles.showMoreTextOpen, styles.showMoreText) : styles.showMoreText
                      }
                    >
                      <span className={styles.innerShowMoreText}>
                        <EscapeNewLine text={category.fields.readMoreDescription} />
                      </span>
                    </p>
                  ) : null}

                  {category.fields.readMoreDescription ? (
                    <button onClick={() => setShowMore(!showMore)} className={styles.showMoreButton}>
                      {showMore ? t("generic.read_less") : t("generic.read_more")}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <ProductNavigationItemsCurrentPath
                navigation={props.navigation}
                listLinks={category.fields.listLinks?.filter(isActiveEntry)}
                changeHeaderLayout={props.changeHeaderLayout}
              />
              <NumberProducts total={totalNumberProducts} type="desktop" />
            </Container>
          </div>
          <NumberProducts total={totalNumberProducts} type="mobile" />
          {props.serverState && props.serverUrl ? (
            <ProductGridSkeleton
              serverUrl={props.serverUrl}
              serverState={props.serverState}
              algoliaIndexId={algoliaIndexId}
              algoliaUserToken={props.algoliaUserToken}
              categoryId={category.fields.categoryId}
              components={props.components}
              listScope={props.listScope}
            />
          ) : (
            <EvaProductListing components={props.components} listScope={props.listScope} query={query} />
          )}
        </Grid>
      </Container>
      {props.componentsAfterListing?.filter(isActiveEntry).map((entry) => (
        <EditorialEntry key={entry.sys.id} entry={entry} />
      ))}
    </div>
  );
}
